import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Save, Loader2, EyeOff } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const MODES = [{ id: "first_last", label: "First + Last" }, { id: "first_initial", label: "First + Initial" }, { id: "first_name", label: "First name" }];
const PT = [["lab", "Complete a lab"], ["task", "Complete a task"], ["badge", "Earn a badge"], ["quiz", "Pass an assessment"], ["challenge", "Complete a challenge lab"]];

export default function AdminLeaderboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, st] = await Promise.all([api.get("/admin/leaderboard/settings"), api.get("/instructor/students")]);
      setS(a.data); setStudents(st.data.students);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!s) return <div className="h-[60vh] grid place-items-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/leaderboard/settings", { enabled: s.enabled, display_mode: s.display_mode, points: s.points, levels: s.levels });
      toast.success("Leaderboard settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const setVisibility = async (id, hidden) => {
    try {
      await api.put(`/admin/students/${id}/leaderboard-visibility`, { hidden });
      setStudents((l) => l.map((x) => (x.id === id ? { ...x, leaderboard_hidden: hidden } : x)));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const filtered = students.filter((x) => (x.name + x.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="admin-leaderboard-page">
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-primary" />
        <div><h1 className="font-display font-bold text-3xl tracking-tight">Leaderboard Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">Configure scoring, achievement levels, display, and privacy.</p></div>
      </div>

      <Tabs defaultValue="config" className="mt-6">
        <TabsList>
          <TabsTrigger value="config" data-testid="tab-lb-config">Scoring & Levels</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-lb-privacy">Privacy</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="mt-4 space-y-4">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <label className="flex items-center justify-between">
              <div><div className="font-medium">Leaderboard enabled</div><div className="text-sm text-muted-foreground">Show the leaderboard to students.</div></div>
              <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="lb-enabled" />
            </label>
            <div className="border-t border-border pt-4">
              <div className="font-medium mb-2">Student display name</div>
              <div className="flex flex-wrap gap-2">
                {MODES.map((m) => <Button key={m.id} size="sm" variant={s.display_mode === m.id ? "default" : "outline"} onClick={() => setS({ ...s, display_mode: m.id })} data-testid={`mode-${m.id}`}>{m.label}</Button>)}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="font-medium mb-3">Point values</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {PT.map(([k, label]) => (
                <label key={k} className="flex items-center justify-between gap-3">
                  <span className="text-sm">{label}</span>
                  <Input type="number" className="w-24" value={s.points[k]} onChange={(e) => setS({ ...s, points: { ...s.points, [k]: Number(e.target.value) } })} data-testid={`pts-${k}`} />
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="font-medium mb-3">Achievement levels (min points)</div>
            <div className="space-y-2">
              {s.levels.map((lv, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={lv.name} onChange={(e) => setS({ ...s, levels: s.levels.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} data-testid={`lvl-name-${i}`} />
                  <Input type="number" className="w-28" value={lv.min_points} onChange={(e) => setS({ ...s, levels: s.levels.map((x, j) => j === i ? { ...x, min_points: Number(e.target.value) } : x) })} data-testid={`lvl-min-${i}`} />
                </div>
              ))}
            </div>
          </div>

          <Button className="gap-2" onClick={save} disabled={saving} data-testid="save-lb">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings</Button>
        </TabsContent>

        <TabsContent value="privacy" className="mt-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground mb-4">Hide a student from the public leaderboard without affecting their learning progress.</p>
            <Input placeholder="Search students…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 max-w-sm" data-testid="lb-student-search" />
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/50 text-left"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3 text-right">Hidden</th></tr></thead>
                <tbody>
                  {filtered.map((x) => (
                    <tr key={x.id} className="border-t border-border" data-testid={`lb-student-${x.id}`}>
                      <td className="p-3 font-medium">{x.name}</td>
                      <td className="p-3 text-muted-foreground">{x.email}</td>
                      <td className="p-3 text-right"><Switch checked={!!x.leaderboard_hidden} onCheckedChange={(v) => setVisibility(x.id, v)} data-testid={`hide-${x.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
